import { inject, Injectable } from '@angular/core';
import { OpenApiHttpResponseError } from '@rxap/open-api';
import {
  IngestControllerCheckRemoteMethod,
  IngestControllerIngestRemoteMethod,
} from 'open-api-iirds/src/lib/remote-methods';
import { defer, from, lastValueFrom, retry, timer } from 'rxjs';

const INITIAL_DELAY_MS = 500;
const MAX_DELAY_MS = 30_000;

/** Thrown when the ingest status keeps reporting 503 past the attempt cap. */
export class RequestApiIngestTimeoutError extends Error {
  constructor() {
    super('The server did not finish preparing the data in time.');
    this.name = 'RequestApiIngestTimeoutError';
  }
}

/**
 * Triggers server-side ingestion of the authorized resources and waits until
 * the graph store is ready, so package/resource requests don't fail with a
 * 503 "not ingested". Extracted from `ingestGuard` (shared poll logic), minus
 * the `IngestingComponent` dialog and `iirds.fetchData()` which only apply to
 * the fixed-source graph viewer.
 */
@Injectable({ providedIn: 'root' })
export class RequestApiIngestService {
  private readonly ingest = inject(IngestControllerIngestRemoteMethod);
  private readonly status = inject(IngestControllerCheckRemoteMethod);

  /**
   * @param onWaiting invoked once when the first 503 "still ingesting"
   *   response arrives, so the caller can reflect a waiting state.
   * @param maxAttempts cap on 503 retries; `Infinity` polls forever (route
   *   guards), a finite value throws {@link RequestApiIngestTimeoutError}.
   */
  async ensureIngested(
    onWaiting?: () => void,
    maxAttempts = Infinity,
  ): Promise<void> {
    await this.ingest.call({ requestBody: {} });
    await lastValueFrom(
      defer(() => from(this.status.call({}))).pipe(
        retry({
          delay: (err, retryCount) => {
            if (
              !(
                err instanceof OpenApiHttpResponseError && err.status === 503
              )
            ) {
              throw err;
            }
            if (retryCount > maxAttempts) {
              throw new RequestApiIngestTimeoutError();
            }
            onWaiting?.();
            return timer(
              Math.min(INITIAL_DELAY_MS * 2 ** (retryCount - 1), MAX_DELAY_MS),
            );
          },
        }),
      ),
    );
  }
}
